/* ========================================================================= *
 * BodyTrades + TradeRollup — the trades on the Governing Bodies page.
 *
 *   TradeRollup  the page strip: every trade, rolled up by the governing body
 *                its records carry, reconciled to the total. A trade touching
 *                NO body (a flagged one) is its own row — never dropped.
 *   BodyTrades   on each card: the trades over THAT card's records. It takes
 *                the card's own `tally.rows`, the same rows the card's count
 *                and assignment stamp use, so the three figures on one card
 *                are over one set of records and cannot disagree.
 * ========================================================================= */
import { Link } from 'react-router-dom'
import { useTradeLinks, refreshTradeLinks } from '../../lib/useTradeLinks.js'
import { tradesForRecords, chipFor, RECONCILE_FAILED, LINK } from '../../lib/tradeLinks.js'
import styles from './Trades.module.css'

const nf = (n) => Number(n || 0).toLocaleString()

export function TradeRollup({ onPickBody = null }) {
  const { status, links, error } = useTradeLinks()
  if (!links) {
    return (
      <section className={`card ${styles.section}`} data-tone={status === 'error' ? 'bad' : undefined}
        aria-label="Trades by governing body">
        <p className={styles.head}>Trades by governing body</p>
        <p className={styles.sentence}>
          {status === 'error'
            ? <>The trade links could not be read: {error} This is a refusal, not &ldquo;no
              trades&rdquo;.</>
            : 'Reading every trade and keying it to its governing body…'}
        </p>
        {status === 'error' ? (
          <div className={styles.controls}>
            <button type="button" className={styles.cta} data-primary="1"
              onClick={() => refreshTradeLinks({ force: true })}>Read the trades again</button>
          </div>
        ) : null}
      </section>
    )
  }
  const t = links.totals
  const rows = links.byBody || []
  const flaggedTrades = links.trades.filter((x) => x.link === LINK.FLAGGED)
  return (
    <section className={`card ${styles.section}`} data-tone={t.flagged || !t.reconciles ? 'bad' : undefined}
      aria-label="Trades by governing body">
      <p className={styles.head}>Trades by governing body</p>
      <p className={styles.recon}>
        <b>{nf(t.trades)}</b> {t.trades === 1 ? 'trade' : 'trades'} = <b>{nf(t.linked)}</b> linked
        + <b>{nf(t.flagged)}</b> flagged, over <b>{nf(t.recordsUnderTrade)}</b>{' '}
        {t.recordsUnderTrade === 1 ? 'record' : 'records'} and <b>{nf(rows.filter((r) => r.body).length)}</b>{' '}
        governing {rows.filter((r) => r.body).length === 1 ? 'body' : 'bodies'}.
        {rows.some((r) => !r.body) ? ' Trades that reach no body are listed below as their own row.' : ''}
        {' '}A trade that spans two bodies is counted on both rows and once in the total.
      </p>
      {!t.reconciles ? <p className={styles.fail} role="alert">{RECONCILE_FAILED}</p> : null}
      {t.trades === 0 ? (
        <>
          <p className={styles.sentence}>
            No trades were returned by this scoped read. This result does not
            establish that no trade has ever existed across the team. Returned trades roll up
            here under their governing body.
          </p>
          <div className={styles.controls}>
            <Link className={styles.cta} data-primary="1" to="/trade-desk">Open the Trade Desk</Link>
          </div>
        </>
      ) : (
        <ul className={styles.list}>
          {rows.map((r) => (
            <li key={r.body || '(none)'} className={styles.order} data-flagged={r.flagged ? '1' : undefined}>
              <div className={styles.bodyRow}>
                <p className={styles.verb}>{r.body || 'No governing body'}</p>
                <span className={styles.recon}>
                  <b>{nf(r.trades)}</b> {r.trades === 1 ? 'trade' : 'trades'} · {nf(r.linked)} linked
                  · {nf(r.flagged)} flagged · {nf(r.records)} {r.records === 1 ? 'record' : 'records'}
                </span>
              </div>
              <div className={styles.controls}>
                {r.body && onPickBody ? (
                  <button type="button" className={styles.cta} data-primary="1"
                    onClick={() => onPickBody(r.body)}>Show this body&rsquo;s card</button>
                ) : null}
                {(r.tradeIds || []).slice(0, 3).map((id) => (
                  <Link key={id} className={styles.cta} to={id.startsWith('proposal:')
                    ? `/command-center?record=${encodeURIComponent(id.slice(9))}`
                    : `/trade-desk?offer=${encodeURIComponent(id)}`}>
                    Open {id.startsWith('proposal:') ? 'the record' : 'the offer'}
                  </Link>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
      {flaggedTrades.length ? (
        <ul className={styles.list} aria-label="Flagged trades">
          {flaggedTrades.map((x) => (
            <li key={x.id} className={styles.order} data-flagged="1">
              <p className={styles.verb}>Flagged — {x.scopeLabel || x.id}</p>
              {x.flags.map((f) => (
                <div key={f.kind + (f.unit || '')}>
                  <p className={styles.reason}>{f.says}</p>
                  <div className={styles.controls}>
                    {f.fix?.to ? (
                      <Link className={styles.cta} data-primary="1" to={f.fix.to}>{f.fix.label}</Link>
                    ) : (
                      <button type="button" className={styles.cta} data-primary="1"
                        onClick={() => refreshTradeLinks({ force: true })}>{f.fix?.label || 'Read again'}</button>
                    )}
                  </div>
                </div>
              ))}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}

export default function BodyTrades({ rows = [] }) {
  const { links } = useTradeLinks()
  if (!links || !rows.length) return null
  const r = tradesForRecords(links, rows.map((x) => x?.id).filter(Boolean))
  if (!r.total) return null
  return (
    <div className={styles.section} data-tone={r.flagged ? 'bad' : undefined} aria-label="Trades on this body">
      <p className={styles.head}>
        Trades — {nf(r.total)} over {nf(r.records)} of this card&rsquo;s{' '}
        {r.records === 1 ? 'record' : 'records'} ({nf(r.linked)} linked + {nf(r.flagged)} flagged)
      </p>
      <div className={styles.chipWrap}>
        {r.items.map(({ trade, n }) => {
          const c = chipFor([trade])
          return (
            <Link key={trade.id} className={styles.chip} data-tone={c.tone} to={c.fix?.to || c.to}
              title={c.title}>
              ⇄ {c.label} · {nf(n)} {n === 1 ? 'record' : 'records'}{c.fix ? ` — fix: ${c.fix.label}` : ''}
            </Link>
          )
        })}
      </div>
    </div>
  )
}
